import React, { useRef, useState } from 'react';

import { Form, Field } from 'react-final-form';
import { useSelector } from 'react-redux';

import Select2 from 'components/Pages/Admin/Itinerary/shared/Select2';
import Preview from 'components/Pages/Admin/shared/Preview';
import ArrayFields from 'components/shared/ArrayFields';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Label from 'components/shared/FormAdapters/FormDefault/Label';
import LabelWrapper from 'components/shared/FormAdapters/FormDefault/Label/Wrapper';
import FormErrorText from 'components/shared/FormErrorText';
import Visibility from 'components/shared/Icon/icons/Visibility';
import Loader from 'components/shared/Loader';
import Modal from 'components/shared/Modal';
import StickyFooter from 'components/shared/StickyFooter/StickyFooter';
import FormTitle from 'components/shared/Typography/FormTitle';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import useElementBottomPosition, { SNACKBAR_POSITION } from 'hooks/useElementBottomPosition';
import { useFormFieldPopup } from 'hooks/useFormFieldPopup';
import { pageDataSelector } from 'store/page/selectors';
import { capitalizeAllWordsForInput } from 'utils/common';
import generateHandleValue from 'utils/form/mutators/generateHandleValue';

import { BuilderFooterButton } from '../../shared/BuilderFooterButton';
import { usePageContext } from '../../shared/context';
import PageFormButtons from '../../shared/PageFormButtons';
import { useHandleAfterNextDetails } from '../../utils';
import { useActivityDetails } from './functions';

const prepareSubmitValues = (values) => {
  return {
    ...values,
    areaId: typeof values.areaId === 'string' ? null : values.areaId,
    areaName: typeof values.areaId === 'string' ? values.areaId : null,
  };
};

function Details() {
  useElementBottomPosition(SNACKBAR_POSITION);
  const page = useSelector(pageDataSelector);
  const { useNextStepFormHandler } = usePageContext();
  const onAfterNext = useHandleAfterNextDetails();
  const handleNextStep = useNextStepFormHandler({ onAfterNext });
  const { countries, validate, onCountryChange, areas, areasBeingLoaded } = useActivityDetails({ page });
  const showPopup = useFormFieldPopup();
  const areaSelectRef = useRef();
  const [areaPreviewState, setAreaPreviewState] = useState({ isOpen: false, pageId: null });

  return (
    <Form
      onSubmit={(values) => handleNextStep(prepareSubmitValues(values))}
      initialValues={page}
      validate={validate}
      mutators={{
        generateHandleValue,
      }}
      render={({ handleSubmit, submitting, invalid, values, submitError, form }) => (
        <form onSubmit={handleSubmit} autoComplete="nope">
          <UnsavedChangesFormWatcher />

          {submitting && <Loader isFillBlock />}
          <FormTitle center>{page.id ? 'Edit Activity Page Content' : 'Create New Content'}</FormTitle>

          <FormTitle hint="Enter information to organize this content so that you can find it easily later.">
            I. Page Details
          </FormTitle>

          <LabelWrapper>
            <Label>Activity Name*</Label>
            <Text accent>The name of a page will appear throughout the client facing itinerary.</Text>
          </LabelWrapper>
          <Field
            component={FormGroupAdapter}
            name="name"
            placeholder="Hot Air Balloon"
            type="text"
            onCustomChange={form.mutators.generateHandleValue}
            onInput={capitalizeAllWordsForInput}
          />

          <Field component={FormGroupAdapter} name="countries" type="inputGroup">
            <Field
              component={FormGroupAdapter}
              name="countryId"
              placeholder="Kenya"
              type="select"
              label="Country"
              options={countries}
              onCustomChange={(countryId) => {
                onCountryChange(countryId, form);
                if (areaSelectRef.current?.onChange) {
                  areaSelectRef.current.onChange(null, { action: 'clear' });
                }
              }}
            />

            <Field
              name="areaId"
              component={FormGroupAdapter}
              customComponent={Select2}
              label="Area"
              placeholder="Maasai Mara"
              defaultOption={{ value: page.areaId, label: page.areaName ?? page.areaHandle }}
              externalOptions={areas}
              useExternalOptions
              isCreatable={Boolean(values.countryId)}
              optionActionIcon={Visibility}
              onOptionActionClick={(option) => {
                setAreaPreviewState({ isOpen: true, pageId: option.pageId });
              }}
              optionCreationProps={{
                title: 'What is the area title?',
                placeholder: 'New Zealand',
                openOptionCreationLabel: 'Create Custom Area',
              }}
              selectRef={areaSelectRef}
              blockMenuClosing={areaPreviewState.isOpen}
              isLoadingExternal={areasBeingLoaded}
              optionSubtitleKey="tagsString"
            />
          </Field>

          <LabelWrapper>
            <Label>Tags</Label>
            <Text accent>
              Tags allow you to create tailored versions of your content pages for different audiences and/or seasons.
              You can leave blank if this is the only version of this page.
            </Text>
          </LabelWrapper>
          <Field
            component={FormGroupAdapter}
            name="tags"
            placeholder="Governors"
            type="tags"
            onCustomChange={form.mutators.generateHandleValue}
            disabled={values?.tags?.length > 2}
            onFocus={showPopup(
              <Text accent style={{ fontSize: '1.5rem' }}>
                <b>Tool Tip:</b>
                <br />
                <br />
                Tags are an easy way to create multiple versions of your Content Page.
                <br />
                <br />
                For example, you might use the following tags to differentiate your page content for Winelands Tour
                pages:
                <br />
                <br />
                private <br />
                <br />
                group <br />
                <br />
                Escape + Explore <br />
                <br />
                Wilderness Journeys <br />
                <br />* Please use sparingly. You are limited to three tags.
              </Text>,
            )}
          />

          <LabelWrapper>
            <Label>@Handle</Label>
            <Text accent>Automatically generated from combining the name and tag fields.</Text>
          </LabelWrapper>
          <Field
            component={FormGroupAdapter}
            name="handle"
            placeholder="@HotAirBalloonGovernors"
            type="text"
            readOnly
          />

          <FormTitle hint="Add on the ground emergency contact for Agents to share with Guests.">
            II. Emergency Contact
          </FormTitle>

          <ArrayFields
            initialValues={page}
            data={{
              sectionName: 'emergencyContacts',
              config: {
                withBlockRemoving: false,
                withBlockAdding: false,
                twoColumn: true,
              },
              fieldsData: [
                {
                  name: 'name',
                  placeholder: 'John Doe',
                  label: 'Name',
                },
                {
                  name: 'phone',
                  placeholder: '555-555-5555',
                  label: 'Phone Number',
                },
                {
                  name: 'email',
                  placeholder: 'john.doe@company.com',
                  label: 'Email',
                  autoComplete: 'none',
                },
              ],
            }}
          />

          {submitError && <FormErrorText>{submitError}</FormErrorText>}

          <StickyFooter>
            <FormActions isRight>
              <BuilderFooterButton htmlType="submit" disabled={submitting || invalid}>
                Next
              </BuilderFooterButton>
            </FormActions>
          </StickyFooter>

          <PageFormButtons prepareValues={prepareSubmitValues} />

          <Field name="areaName">{() => null}</Field>

          <Modal
            noPadding
            size="lg"
            isOpen={areaPreviewState.isOpen}
            toClose={() => setAreaPreviewState({ isOpen: false })}
            children={<Preview pageId={areaPreviewState.pageId} />}
          />
        </form>
      )}
    />
  );
}

export default Details;
